import { imageUrlBuilder, portableTextSerializer } from '@/utils/helpers';

import BlockContent from '@sanity/block-content-to-react';
import CtaButton from './ui/buttons/CtaButton';
import Image from 'next/image';
import QuoteMark from './ui/icons/QuoteMark';
import React from 'react';
import breakpoint from 'styled-components-breakpoint';
import { rem } from 'polished';
import styled, { css } from 'styled-components';
import { switchProp, theme } from 'styled-tools';

const Section = styled.section<{ mode: string }>`
  padding: 4em 2em 2em;
  background: ${({ mode }) =>
    mode === 'dark' ? 'linear-gradient(100.86deg, #2F3F56 8.07%, #5172A1 95.1%)' : '#f2f2f2'};

  .cta-wrapper {
    position: relative;
    text-align: center;
    margin: 2rem auto;
  }
  ${breakpoint('xs', 'sm')`
  padding: 4em 1.5em 2em;
  `}
`;

const Blockquote = styled.blockquote<{ mode: string }>`
  position: relative;
  padding: 2rem 0;
  font-weight: normal;
  font-size: ${rem(20)};
  ${breakpoint('xs', 'sm')`
    font-size: ${rem(16)};
    padding: 1rem 0;
  `}

  .quoteline {
    line-height: 1.1em;
    margin-bottom: 2rem;
    font-size: 36px;
    font-weight: 700;
    text-align: center;
    color: ${({ mode }) => (mode === 'dark' ? 'white' : theme('colors.blue'))};

    strong:first-child {
      color: ${theme('colors.orange')};
      font-weight: bold;
    }
  }

  .cite {
    display: block;
    font-size: 14px;
    font-weight: 400;
    color: ${theme('colors.orange')};
    font-style: normal;
    text-align: center;
  }

  ${breakpoint('xs', 'sm')`
    .quoteline {
      font-size: 24px;
    }
  `}

  ${breakpoint('sm', 'md')`
    .quoteline {
      font-size: 28px;
    }
  `}
`;

const QuoteWrapper = styled.div`
  position: relative;
  text-align: center;
  width: 89px;
  height: 89px;
  margin: 0 auto;
  & svg {
    fill: none;
  }

  img {
    object-fit: cover;
    width: 89px;
    height: 89px;
    margin: 0 auto;
    border-radius: 50%;
    border: 3px solid #f88143;
  }
`;

const QuoteMarkLine = styled.hr<{ layoutLine?: string }>`
  background: ${theme('colors.orange')};
  position: absolute;
  padding: 0;
  margin: 0;
  border: 0;
  overflow: hidden;

  ${switchProp('layoutLine', {
    left: css`
      height: 3px;
      width: 64px;
      top: calc(50% - 3px);
      left: -64px;
    `,
    right: css`
      height: 3px;
      width: 64px;
      top: calc(50% - 3px);
      right: -64px;
    `,
    // review top and bottom
    top: css`
      height: 64px;
      width: 3px;
      top: -64px;
      left: calc(50% - 3px);
    `,
    bottom: css`
      height: 64px;
      width: 3px;
      bottom: -64px;
      left: calc(50% - 3px);
    `,
    none: css`
      display: none;
    `,
  })}
`;

interface TestimonialProps {
  title?: unknown;
  cite?: unknown;
  author?: unknown;
  cta?: unknown;
  authorImg?: unknown;
  textColor?: unknown;
  bgColor?: unknown;
  bgGradient?: unknown;
  quoteline?: unknown;
  image?: unknown;
  mode?: string;
  layout?: string;
  extraText?: string;
}

const Testimonials: React.FC<TestimonialProps> = ({
  cite,
  author,
  cta,
  authorImg,
  quoteline,
  image,
  mode = 'dark',
  layout = 'top',
  extraText,
}) => {
  const imgUrl =
    authorImg &&
    imageUrlBuilder
      .image((authorImg as { asset: unknown }).asset)
      .width(120)
      .auto('format')
      .quality(80)
      .url();
  return (
    <Section mode={mode}>
      <QuoteWrapper>
        <QuoteMarkLine layoutLine={layout} />
        {image || imgUrl ? (
          <Image src={(image as string) || imgUrl} alt={(author as string) || ''} fill />
        ) : (
          <QuoteMark />
        )}
      </QuoteWrapper>
      <Blockquote mode={mode}>
        <div className="quoteline">
          {(cite && <BlockContent blocks={cite} serializers={portableTextSerializer} />) || `"${quoteline as string}"`}
        </div>
        <cite className="cite">{author as string}</cite>
        {extraText && <cite className="cite">{extraText}</cite>}
      </Blockquote>
      {cta && (
        <div className="cta-wrapper">
          <CtaButton className="cta-secondary" to={(cta as { url: string }).url}>
            {(cta as { text?: string; title?: string }).text || (cta as { text?: string; title?: string }).title}
          </CtaButton>
        </div>
      )}
    </Section>
  );
};

export default Testimonials;
